import React, { useEffect, useMemo, useRef, useState } from "react";
import { Upload, Save, RefreshCw, ImageIcon, Check } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { SERVICES } from "../lib/services";
import { imageForSub } from "../lib/serviceImages";
import { adminSaveOverride, adminUploadImage, publicOverrides, absoluteUrl } from "../lib/api";

export default function AdminContentPage() {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [drafts, setDrafts] = useState({}); // { [code]: { price, image } }
  const fileInputs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const data = await publicOverrides();
      setOverrides(data.overrides || {});
    } catch (e) {
      toast.error("Failed to load overrides");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => {
    const out = [];
    for (const svc of SERVICES) {
      for (const sub of svc.subs) {
        out.push({ svc, sub });
      }
    }
    return out;
  }, []);

  const currentPrice = (code, fallback) => {
    const draft = drafts[code]?.price;
    if (draft !== undefined) return draft;
    if (overrides[code]?.price !== undefined && overrides[code]?.price !== null) return overrides[code].price;
    return fallback;
  };
  const currentImage = (code, fallback) => {
    const draft = drafts[code]?.image;
    if (draft !== undefined) return draft;
    if (overrides[code]?.image) return overrides[code].image;
    return fallback || imageForSub(code) || "";
  };

  const setDraft = (code, key, value) => {
    setDrafts((d) => ({ ...d, [code]: { ...(d[code] || {}), [key]: value } }));
  };

  const hasDraft = (code) => !!drafts[code] && Object.keys(drafts[code]).length > 0;

  const save = async (code) => {
    if (!hasDraft(code)) return;
    setSaving((s) => ({ ...s, [code]: true }));
    try {
      const payload = { ...(drafts[code] || {}) };
      const data = await adminSaveOverride(code, payload);
      setOverrides((o) => ({ ...o, [code]: data.override || { ...o[code], ...payload } }));
      setDrafts((d) => {
        const next = { ...d };
        delete next[code];
        return next;
      });
      // Notify studio pages to refetch overrides
      window.dispatchEvent(new CustomEvent("authoros:overrides-updated"));
      toast.success(`Saved ${code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving((s) => ({ ...s, [code]: false }));
    }
  };

  const upload = async (code, file) => {
    if (!file) return;
    setSaving((s) => ({ ...s, [code]: true }));
    try {
      const data = await adminUploadImage(file);
      const url = data.url;
      // Immediately persist image override too
      const merged = { image: url, ...(drafts[code]?.price !== undefined ? { price: drafts[code].price } : {}) };
      const saved = await adminSaveOverride(code, merged);
      setOverrides((o) => ({ ...o, [code]: saved.override || { ...o[code], image: url } }));
      setDrafts((d) => {
        const next = { ...d };
        delete next[code];
        return next;
      });
      window.dispatchEvent(new CustomEvent("authoros:overrides-updated"));
      toast.success(`Image updated for ${code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setSaving((s) => ({ ...s, [code]: false }));
    }
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">Studio admin</div>
          <h1 className="mt-2 font-serif-display text-[46px] font-medium leading-[0.98] tracking-tight">
            Content & Pricing<span className="text-brand">.</span>
          </h1>
          <p className="mt-2 max-w-[560px] text-[13px] text-brand-ivory/60">
            Edit any sub-service's displayed price or replace its image. Changes go live on the customer app instantly.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 rounded-md border border-brand-hair px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.28em] text-brand-ivory hover:border-brand hover:text-brand"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Reload
        </button>
      </div>

      <div className="mt-8 space-y-8">
        {SERVICES.map((svc) => (
          <div key={svc.slug}>
            <div className="mb-3 flex items-center gap-3 font-mono text-[10.5px] uppercase tracking-[0.28em] text-brand-mute">
              <span className="text-brand">{svc.id}</span>
              <span className="text-brand-ivory">{svc.label}</span>
              <span className="text-brand-mute/60">— {svc.subs.length} sub-services</span>
            </div>
            <div className="overflow-hidden rounded-xl border border-brand-hair bg-brand-panel">
              <div className="overflow-x-auto">
                <div className="min-w-[760px]">
                  <div className="grid grid-cols-[80px_1fr_180px_180px_130px] gap-4 border-b border-brand-hair px-6 py-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                    <div>Code</div>
                    <div>Title</div>
                    <div>Price</div>
                    <div>Image</div>
                    <div />
                  </div>
                  {svc.subs.map((sub) => {
                    const code = sub.code;
                    const busy = !!saving[code];
                    const price = currentPrice(code, sub.price);
                    const image = currentImage(code, sub.image);
                    const dirty = hasDraft(code);
                    return (
                      <div
                        key={code}
                        className="grid grid-cols-[80px_1fr_180px_180px_130px] items-center gap-4 border-b border-brand-hair/60 px-6 py-4"
                      >
                        <div className="font-mono text-[11px] text-brand">{code}</div>
                        <div className="min-w-0">
                          <div className="truncate font-serif-display text-[15px] text-brand-ivory">{sub.title}</div>
                          <div className="truncate font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">
                            {sub.subtitle || sub.billing}
                          </div>
                        </div>
                        <input
                          value={price ?? ""}
                          onChange={(e) => setDraft(code, "price", e.target.value)}
                          className={`w-full rounded-md border bg-brand-panel2 px-3 py-2 font-mono text-[12.5px] text-brand-ivory focus:border-brand ${
                            drafts[code]?.price !== undefined ? "border-brand" : "border-brand-hair"
                          }`}
                        />
                        <div className="flex items-center gap-2">
                          <div className="flex h-10 w-14 flex-none items-center justify-center overflow-hidden rounded-md border border-brand-hair bg-brand-panel2 text-brand-mute">
                            {image ? (
                              <img src={absoluteUrl(image)} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <ImageIcon className="h-4 w-4" />
                            )}
                          </div>
                          <input
                            ref={(el) => (fileInputs.current[code] = el)}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => upload(code, e.target.files?.[0])}
                          />
                          <button
                            onClick={() => fileInputs.current[code]?.click()}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 rounded-md border border-brand-hair px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-ivory hover:border-brand hover:text-brand disabled:opacity-60"
                          >
                            <Upload className="h-3.5 w-3.5" /> Upload
                          </button>
                        </div>
                        <button
                          onClick={() => save(code)}
                          disabled={!dirty || busy}
                          className={`inline-flex items-center justify-center gap-2 rounded-md py-2 font-mono text-[10px] uppercase tracking-[0.28em] ${
                            dirty
                              ? "bg-brand text-white hover:bg-[#ff5c26]"
                              : "border border-brand-hair text-brand-mute"
                          }`}
                        >
                          {busy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : dirty ? <Save className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
                          {dirty ? "Save" : "Saved"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
